import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FaCheckCircle, FaClipboard, FaExclamationTriangle, FaInfoCircle, FaTimes } from 'react-icons/fa';
import { useTheme } from '../themeConfig';

const FeedbackContext = createContext(null);

const TYPE_ICON = {
  success: <FaCheckCircle />,
  error: <FaExclamationTriangle />,
  warning: <FaExclamationTriangle />,
  info: <FaInfoCircle />,
};

const normalizeOptions = (options, fallbackTitle = 'Notice') => {
  if (typeof options === 'string') {
    return { title: fallbackTitle, message: options };
  }
  return { title: fallbackTitle, ...(options || {}) };
};

export const useFeedback = () => {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedback must be used inside FeedbackProvider');
  }
  return context;
};

export const FeedbackProvider = ({ children }) => {
  const { theme } = useTheme();
  const [toasts, setToasts] = useState([]);
  const [modal, setModal] = useState(null);
  const activeResolveRef = useRef(null);

  const closeModal = useCallback((value) => {
    const resolve = activeResolveRef.current;
    activeResolveRef.current = null;
    setModal(null);
    resolve?.(value);
  }, []);

  const toast = useCallback((options) => {
    const next = normalizeOptions(options, 'Lumirae');
    const id = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const duration = Number(next.duration || 3600);
    setToasts((current) => [...current, { id, type: next.type || 'info', ...next }]);
    window.setTimeout(() => {
      setToasts((current) => current.filter((item) => item.id !== id));
    }, duration);
  }, []);

  const alert = useCallback((options) => {
    const next = normalizeOptions(options, 'Notice');
    const { type: tone, ...rest } = next;
    return new Promise((resolve) => {
      activeResolveRef.current = resolve;
      setModal({
        type: 'alert',
        tone: tone || 'info',
        confirmLabel: next.confirmLabel || 'Got it',
        ...rest,
      });
    });
  }, []);

  const confirm = useCallback((options) => {
    const next = normalizeOptions(options, 'Are you sure?');
    const { type: tone, ...rest } = next;
    return new Promise((resolve) => {
      activeResolveRef.current = resolve;
      setModal({
        type: 'confirm',
        tone: tone || 'warning',
        confirmLabel: next.confirmLabel || 'Confirm',
        cancelLabel: next.cancelLabel || 'Cancel',
        ...rest,
      });
    });
  }, []);

  const copyText = useCallback(async ({ title = 'Copy Link', message = 'Copy this value.', value, successMessage = 'Copied.' }) => {
    const text = String(value || '');
    if (!text) return false;
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(text);
      toast({ type: 'success', title, message: successMessage });
      return true;
    } catch {
      return new Promise((resolve) => {
        activeResolveRef.current = resolve;
        setModal({
          type: 'copy',
          tone: 'info',
          title,
          message,
          value: text,
          confirmLabel: 'Done',
        });
      });
    }
  }, [toast]);

  const value = useMemo(() => ({
    toast,
    alert,
    confirm,
    copyText,
  }), [alert, confirm, copyText, toast]);

  return (
    <FeedbackContext.Provider value={value}>
      {children}

      <div className="pointer-events-none fixed inset-x-0 top-4 z-[5200] flex flex-col items-center gap-3 px-4">
        <AnimatePresence>
          {toasts.map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: -18, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -12, scale: 0.96 }}
              className="lumirae-feedback-toast lumirae-ds-panel pointer-events-auto flex w-full max-w-sm items-start gap-3 px-4 py-3 shadow-2xl"
              style={{ borderColor: item.type === 'error' ? 'var(--lumirae-state-error)' : theme.cardBorder }}
            >
              <span
                className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-2xl"
                style={{
                  backgroundColor: item.type === 'error' ? 'color-mix(in srgb, var(--lumirae-state-error) 12%, white)' : theme.accentSoft,
                  color: item.type === 'error' ? 'var(--lumirae-state-error)' : theme.accent,
                }}
              >
                {TYPE_ICON[item.type] || TYPE_ICON.info}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-black uppercase tracking-[0.16em]" style={{ color: theme.text }}>
                  {item.title}
                </div>
                {item.message && (
                  <div className="mt-1 text-xs font-bold leading-relaxed" style={{ color: theme.secondaryText || theme.text }}>
                    {item.message}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="lumirae-ds-icon-button h-8 w-8"
                onClick={() => setToasts((current) => current.filter((toastItem) => toastItem.id !== item.id))}
                aria-label="Dismiss notice"
              >
                <FaTimes size={12} />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {modal && (
          <motion.div
            className="fixed inset-0 z-[5300] flex items-center justify-center bg-[#221D24]/62 p-4 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 18, scale: 0.96 }}
              className="lumirae-ds-panel w-full max-w-md p-6 shadow-2xl"
              style={{
                '--lumirae-accent-primary': theme.accent,
                '--lumirae-accent-soft': theme.accentSoft,
                '--lumirae-surface-raised': theme.cardBg || '#FFFDF8',
                '--lumirae-border-default': theme.cardBorder || 'rgba(221, 205, 190, 0.9)',
                '--lumirae-text-primary': theme.text || '#251F21',
                '--lumirae-text-secondary': theme.secondaryText || '#5F5552',
                backgroundColor: theme.cardBg || '#FFFDF8',
                borderColor: theme.cardBorder || 'rgba(221, 205, 190, 0.9)',
                color: theme.text || '#251F21',
              }}
            >
              <div className="flex items-start gap-4">
                <div
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[1.25rem]"
                  style={{
                    backgroundColor: modal.tone === 'error' ? 'color-mix(in srgb, var(--lumirae-state-error) 12%, white)' : theme.accentSoft,
                    color: modal.tone === 'error' ? 'var(--lumirae-state-error)' : theme.accent,
                  }}
                >
                  {modal.type === 'copy' ? <FaClipboard /> : TYPE_ICON[modal.tone] || TYPE_ICON.info}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg font-black leading-tight tracking-tight" style={{ color: theme.text || '#251F21' }}>
                    {modal.title}
                  </h3>
                  {modal.message && (
                    <p className="mt-2 text-sm font-bold leading-relaxed" style={{ color: theme.text || '#251F21' }}>
                      {modal.message}
                    </p>
                  )}
                </div>
              </div>

              {modal.type === 'copy' && (
                <textarea
                  readOnly
                  value={modal.value}
                  onFocus={(event) => event.target.select()}
                  className="lumirae-ds-input mt-5 min-h-[96px] resize-none p-4 text-xs font-bold leading-relaxed"
                />
              )}

              <div className={`mt-6 grid gap-3 ${modal.type === 'confirm' ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {modal.type === 'confirm' && (
                  <button
                    type="button"
                    className="lumirae-ds-button lumirae-ds-button-secondary px-5 py-4"
                    onClick={() => closeModal(false)}
                  >
                    {modal.cancelLabel}
                  </button>
                )}
                <button
                  type="button"
                  className={`lumirae-ds-button px-5 py-4 ${modal.tone === 'error' ? 'lumirae-ds-button-danger' : 'lumirae-ds-button-primary'}`}
                  onClick={() => closeModal(modal.type === 'confirm' ? true : true)}
                >
                  {modal.confirmLabel}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </FeedbackContext.Provider>
  );
};

export default FeedbackProvider;
